"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-2">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-9 w-24 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-accent/50")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-12 w-full bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="flex items-center py-4 text-lg font-medium border-b border-border/40 text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-8 flex flex-col gap-4">
        <Button variant="outline" className="w-full justify-center h-12 text-base" onClick={onClose}>
          Driver Login
        </Button>
      </div>
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  return <section id="main-navigation" className="sticky top-0 z-50 w-full">
      <header className={cn("w-full transition-all duration-300 border-b", isScrolled ? "bg-background/90 backdrop-blur-md border-border shadow-sm" : "bg-background border-border/40")}>
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          {}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588353/img/gigfleet_logo.png" alt="GigFleet" className="h-7 w-auto object-contain" />
          </Link>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-2 md:gap-4 shrink-0">
            <Button variant="outline" className="hidden md:inline-flex font-semibold">
              Driver Login
            </Button>
            
            <div className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-accent/50 transition-colors">
              <CartIcon className="text-foreground" iconSize="size-5" />
            </div>

            <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="size-6" />
            </Button>
          </div>
        </div>
      </header>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-[100] md:hidden">
          {}
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity" onClick={() => setIsMobileMenuOpen(false)} />
          
          {}
          <div className="absolute top-0 right-0 w-[85%] max-w-sm h-full bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right-full duration-300">
            <div className="flex items-center justify-between p-6 border-b border-border/40">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588353/img/gigfleet_logo.png" alt="GigFleet" className="h-6 w-auto object-contain" />
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="text-muted-foreground hover:text-foreground">
                <X className="size-6" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="16">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>
          </div>
        </div>}
    </section>;
}